/**
 * Centralized PII / secret redaction for application logs.
 *
 * Used by the structured logger to scrub sensitive values before they
 * are emitted to stdout / log aggregators. Audit rows in the DB are NOT
 * routed through this — they intentionally retain raw IDs for admin
 * accountability (see sanitizeAuditData in ./pii.ts for that path).
 */

import { maskEmail } from "./pii";

// --- Regex patterns -----------------------------------------------------

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
// E.164-ish and common international phone formats with 8+ digits.
const PHONE_RE = /(?:\+?\d[\s\-().]?){8,}\d/g;
const IPV4_RE = /\b(?:\d{1,3}\.){3}\d{1,3}\b/g;
const IPV6_RE = /\b(?:[A-Fa-f0-9]{1,4}:){2,7}[A-Fa-f0-9]{1,4}\b/g;
const JWT_RE = /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{4,}\b/g;
const BEARER_RE = /Bearer\s+[A-Za-z0-9._\-]+/gi;
const STRIPE_KEY_RE = /\b(?:sk|pk|rk)_(?:live|test)_[A-Za-z0-9]{8,}\b/g;
const STRIPE_WHSEC_RE = /\bwhsec_[A-Za-z0-9]{8,}\b/g;
const STRIPE_ID_RE = /\b(?:cus|sub|pi|ch|in|src|seti|tok|re|si|prod|price|evt)_[A-Za-z0-9]{6,}\b/g;
const TELEGRAM_BOT_RE = /\b\d{6,12}:[A-Za-z0-9_-]{30,}\b/g;
// Stripe webhook signature header: t=...,v1=hex
const STRIPE_SIG_RE = /\bt=\d{8,},\s*v1=[a-f0-9]{32,}\b/gi;
// Generic high-entropy hex/secret tokens (api keys, signatures)
const HEX_SECRET_RE = /\b[a-f0-9]{40,}\b/gi;
const BASE64_SECRET_RE = /\b[A-Za-z0-9+/]{40,}={0,2}\b/g;

const SENSITIVE_KEY_PATTERNS = [
  /^authorization$/i,
  /^cookie$/i,
  /^set-cookie$/i,
  /token$/i,
  /^.*api[_-]?key$/i,
  /secret/i,
  /password/i,
  /signature$/i,
  /^x-(stripe|webhook|hub)-signature/i,
  /^stripe[_-]signature$/i,
  /^session([_-]id)?$/i,
  /^refresh_token$/i,
  /^access_token$/i,
];

const PARTIAL_REDACT_KEYS = new Set([
  "stripe_customer_id",
  "stripe_subscription_id",
  "stripe_payment_intent_id",
  "customer",
  "subscription",
  "payment_intent",
  "charge",
  "invoice",
]);

function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEY_PATTERNS.some((re) => re.test(key));
}

function redactStripeId(id: string): string {
  // Keep prefix + last 4 so logs remain correlatable but not exposing.
  const m = /^([a-z]+_)([A-Za-z0-9]+)$/.exec(id);
  if (!m) return "[REDACTED_ID]";
  const tail = m[2].slice(-4);
  return `${m[1]}***${tail}`;
}

/** Stable, non-reversible short hash for correlation (NOT cryptographically secure). */
export function hashIdentifier(value: string | number | null | undefined): string {
  if (value == null) return "";
  const s = String(value);
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `h_${h.toString(16).padStart(8, "0")}`;
}

/** Redact a single string value. */
export function redactPII(value: unknown): unknown {
  if (typeof value !== "string") return value;
  let s = value;
  s = s.replace(STRIPE_SIG_RE, "[REDACTED_STRIPE_SIG]");
  s = s.replace(BEARER_RE, "Bearer [REDACTED]");
  s = s.replace(JWT_RE, "[REDACTED_JWT]");
  s = s.replace(STRIPE_KEY_RE, "[REDACTED_STRIPE_KEY]");
  s = s.replace(STRIPE_WHSEC_RE, "[REDACTED_WHSEC]");
  s = s.replace(TELEGRAM_BOT_RE, "[REDACTED_TG_BOT]");
  s = s.replace(STRIPE_ID_RE, (id) => redactStripeId(id));
  s = s.replace(EMAIL_RE, (e) => maskEmail(e));
  s = s.replace(IPV4_RE, "[REDACTED_IP]");
  s = s.replace(IPV6_RE, "[REDACTED_IP]");
  s = s.replace(PHONE_RE, "[REDACTED_PHONE]");
  // Run high-entropy sweeps last so we don't clobber structured replacements above.
  s = s.replace(HEX_SECRET_RE, "[REDACTED_SECRET]");
  s = s.replace(BASE64_SECRET_RE, (m) =>
    // Avoid mangling normal long base64 prose; only redact if it looks key-ish (mixed case + digits).
    /[A-Z]/.test(m) && /[a-z]/.test(m) && /\d/.test(m) ? "[REDACTED_SECRET]" : m,
  );
  return s;
}

/** Recursively redact metadata objects/arrays passed to the logger. */
export function redactLogMetadata(input: unknown, depth = 0): unknown {
  if (depth > 6) return "[REDACTED_DEPTH]";
  if (input == null) return input;
  if (typeof input === "string") return redactPII(input);
  if (typeof input !== "object") return input;
  if (Array.isArray(input)) return input.map((v) => redactLogMetadata(v, depth + 1));

  if (input instanceof Error) {
    return {
      name: input.name,
      message: redactPII(input.message),
      stack: typeof input.stack === "string" ? redactPII(input.stack) : undefined,
    };
  }

  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
    if (isSensitiveKey(k)) {
      out[k] = "[REDACTED]";
      continue;
    }
    if (PARTIAL_REDACT_KEYS.has(k.toLowerCase()) && typeof v === "string") {
      out[k] = redactStripeId(v);
      continue;
    }
    if (k.toLowerCase() === "email" && typeof v === "string") {
      out[k] = maskEmail(v);
      continue;
    }
    if ((k.toLowerCase() === "ip" || k.toLowerCase() === "remote_addr") && typeof v === "string") {
      out[k] = "[REDACTED_IP]";
      continue;
    }
    out[k] = redactLogMetadata(v, depth + 1);
  }
  return out;
}
